import React, { useCallback, useMemo, useState } from 'react';

import Button from '@/common/components/atoms/Button/Button';
import {
  MEDIA_TYPES,
  NEXT,
  PREV,
  SLIDER_MEDIA_THUMBNAIL_FORMAT,
} from '@/common/constants';
import getImageBySize from '@/common/helpers/getImageBySize';

import CustomArrow from './CustomArrow/CustomArrow';
import CustomPaging from './CustomPaging/CustomPaging';

const useSliderMedia = ({ name, slider }) => {
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [initialSlide, setInitialSlide] = useState(0);

  /**
   * Take media and return image alt or name
   * @param {object} item : media
   * @returns {string}
   */
  const getImageAlt = useCallback((item) => item?.alt || name, [name]);

  /**
   * Take media and return image src or media path or empty string
   * @param {object} item : media
   * @param {string} format : image format
   * @returns {string}
   */
  const getImageSrc = useCallback((item, format) => {
    if (item?.type === MEDIA_TYPES.IMAGE) {
      return item?.url ? getImageBySize(item.url, format) : '';
    }

    return item?.path || '';
  }, []);

  const sliderSettings = useMemo(
    () => ({
      dots: true,
      dotsClass: 'slider-media__nav-list',
      arrows: false,
      slidesToShow: 1,
      customPaging: (i) => (
        <Button>
          <CustomPaging
            alt={getImageAlt(slider[i])}
            path={getImageSrc(slider[i], SLIDER_MEDIA_THUMBNAIL_FORMAT)}
            type={slider[i]?.type}
          />
        </Button>
      ),
    }),
    [getImageAlt, getImageSrc, slider],
  );

  const sliderModalSettings = useMemo(
    () => ({
      dots: true,
      dotsClass: 'slider-media-modal__dots',
      arrows: true,
      infinite: false,
      slidesToShow: 1,
      slidesToScroll: 1,
      initialSlide,
      prevArrow: <CustomArrow type={PREV} />,
      nextArrow: <CustomArrow type={NEXT} />,
      appendDots: (dots) => (
        <ul className="slider-media-modal__dots">{dots}</ul>
      ),
      customPaging: (i) => <Button>{i + 1}</Button>,
    }),
    [initialSlide],
  );

  const handleClickItem = useCallback((index) => {
    setInitialSlide(index);
    setIsOpenModal(true);
  }, []);

  const handleCloseModal = useCallback(() => setIsOpenModal(false), []);

  return {
    getImageAlt,
    getImageSrc,
    handleClickItem,
    handleCloseModal,
    isOpenModal,
    sliderModalSettings,
    sliderSettings,
  };
};

export default useSliderMedia;
